import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import * as firebase from 'firebase/app';
import 'firebase/remote-config';
import { DEFAULT_FEATURE_FLAGS, FeatureFlags } from '../../interfaces/feature-flags.interface';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagsService {
  private flags$ = new BehaviorSubject<FeatureFlags>(DEFAULT_FEATURE_FLAGS);
  private remoteConfig: firebase.remoteConfig.RemoteConfig | null = null;
  private initialized = false;

  constructor() {
    this.initializeDefaults();
  }

  /**
   * Define os valores padrão das flags
   * Estes valores serão usados caso o Firebase falhe ou não esteja configurado
   */
  private initializeDefaults(): void {
    this.flags$.next(DEFAULT_FEATURE_FLAGS);
  }

  /**
   * Inicializa o Firebase Remote Config e carrega as flags
   * Este método deve ser chamado no APP_INITIALIZER
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    try {
      if (!firebase.apps.length) {
        return;
      }

      this.remoteConfig = firebase.remoteConfig();

      if (!this.remoteConfig) {
        return;
      }

      this.remoteConfig.settings = {
        minimumFetchIntervalMillis: environment.production ? 60000 : 0,
        fetchTimeoutMillis: 60000
      };

      this.remoteConfig.defaultConfig = DEFAULT_FEATURE_FLAGS as unknown as { [key: string]: string | number | boolean };

      await this.remoteConfig.fetchAndActivate();
      this.loadFlags();
      this.initialized = true;
    } catch (error) {
      console.error('[FeatureFlags] Erro ao inicializar:', error);
    }
  }

  /**
   * Carrega as flags do Firebase Remote Config
   */
  private loadFlags(): void {
    if (!this.remoteConfig) {
      return;
    }

    try {
      const flags: FeatureFlags = {
        stockFeatureTest: this.remoteConfig.getBoolean('stock_feature_test'),
      };

      this.flags$.next(flags);
    } catch (error) {
      console.error('[FeatureFlags] Erro ao carregar flags:', error);
    }
  }

  /**
   * Verifica se uma feature flag está habilitada
   * 
   * @param flagName Nome da flag a verificar
   * @returns true se a flag está habilitada, false caso contrário
   */
  isEnabled(flagName: keyof FeatureFlags): boolean {
    const value = this.flags$.value[flagName];
    return value === true;
  }

  /**
   * Obtém o valor de uma flag específica
   * 
   * @param flagName Nome da flag
   * @returns Valor da flag
   */
  getFlag(flagName: keyof FeatureFlags): boolean {
    return this.flags$.value[flagName];
  }

  /**
   * Retorna um Observable com todas as flags
   * Use para reagir a mudanças nas flags
   */
  getFlags(): Observable<FeatureFlags> {
    return this.flags$.asObservable();
  }

  /**
   * Obtém todas as flags atuais (snapshot)
   */
  getAllFlags(): FeatureFlags {
    return { ...this.flags$.value };
  }

  /**
   * Força a atualização das flags do Firebase
   * Útil para refresh manual das configurações
   */
  async refresh(): Promise<void> {
    if (!this.remoteConfig) {
      return;
    }

    try {
      await this.remoteConfig.fetchAndActivate();
      this.loadFlags();
    } catch (error) {
      console.error('[FeatureFlags] Erro ao atualizar flags:', error);
    }
  }

  /**
   * Método útil para debug - imprime todas as flags no console
   */
  debug(): void {
    console.log('[FeatureFlags] Estado atual das flags:', this.getAllFlags());
    console.log('[FeatureFlags] Inicializado:', this.initialized);
  }
}
